
import React, { useState, useEffect, lazy, Suspense, useRef, useCallback } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { PullToRefresh } from './components/PullToRefresh';
import { DivineReminder } from './components/DivineReminder';
import { DivineAlarmOverlay } from './components/DivineAlarmOverlay';
import { MiniPlayer } from './components/player/MiniPlayer';
import { PersistentVideoPlayer } from './components/player/PersistentVideoPlayer';
import { videoMiniStore } from './services/videoMiniStore';
import { LanguageProvider, useLanguage } from './components/LanguageContext';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import { NotificationPrompt } from './components/NotificationPrompt';

// Debug banner removed — caching loop fixed
import { useAppTour } from './components/AppTour';
import { FavoritesProvider } from './context/FavoritesContext';
import { MusicProvider } from './context/MusicContext';
import SyncStatusIndicator from './components/SyncStatusIndicator';
import { checkForUpdates } from './services/appUpdater';
import { initAllNotificationChannels, setupDisableGuard } from './services/notificationChannels';
import { scheduleNativeEkadashi, scheduleNativeShlok } from './services/nativeAlarmBridge';
import { isNativeAudioAvailable, nativePlayAudio, nativeStopAudio, nativeGetAudioState } from './services/nativeAudio';
import { EKADASHI_DATES } from './types';
import { Toaster, toast } from './components/ui/sonner';
import { ErrorBoundary } from './components/ErrorBoundary';
import { OfflineBanner } from './components/OfflineBanner';
import { SplashScreen } from './components/SplashScreen';
import { getCurrentUser, saveUserData, logoutUser, getBranding, fetchBrandingFromServer, fetchBannersFromServer, fetchSocialLinksFromServer, fetchContactInfoFromServer, getContactInfo, getNotifications, markNotificationRead, markAllNotificationsRead, syncGoogleUser, getCachedUser } from './services/mockService';
import { UserProfile, Tab, BrandingConfig, ContactInfo, SatsangAudio, AppNotification } from './types';
import { LayoutDashboard, Award, Shield, User, Music, Sparkles, Users, Scan, Lock, Heart, Loader2, Home } from 'lucide-react';

// Eager load: Main 4 tabs (instant navigation)
import { StoreSidebar } from './components/StoreSidebar';
import * as JaapInterfaceModule from './components/JaapInterface';
const JaapInterface = (JaapInterfaceModule as any).JaapInterface || (JaapInterfaceModule as any).default;
import { ShlokaCard, ShlokaModal } from './components/ShlokaOfTheDay';
import { QuickAccessBar } from './components/QuickAccessBar';
import { AchievementBadges, BadgeShowcase } from './components/AchievementBadges';
import { PersonalizedDashboard, saveDailyStats } from './components/PersonalizedDashboard';
import { CommunityLeaderboard, CommunityCard, updateCommunityContribution } from './components/CommunityLeaderboard';
import { DailyReminders, ReminderStatusCard, startReminderChecker } from './components/DailyReminders';
import { EkadashiReminder, EkadashiBanner } from './components/EkadashiReminder';
import { WelcomeModal } from './components/WelcomeModal';
import { AppTour } from './components/AppTour';
import SnoozeModal from './components/SnoozeModal';
import { API_URL } from './services/apiConfig';

// Lazy load: Sidebar features & rarely used pages
const SatsangPlayerPage = lazy(() => import('./components/player/SatsangPlayerPage').then(m => ({ default: m.SatsangPlayerPage })));
const NotificationCenter = lazy(() => import('./components/NotificationCenter').then(m => ({ default: m.NotificationCenter })));
const AkhandJaapRooms = lazy(() => import('./components/AkhandJaapRooms').then(m => ({ default: m.AkhandJaapRooms })));
const SadhanaInsights = lazy(() => import('./components/SadhanaInsights').then(m => ({ default: m.SadhanaInsights })));
const NidhiOCR = lazy(() => import('./components/NidhiOCR').then(m => ({ default: m.NidhiOCR })));
const Dashboard = lazy(() => import('./components/Dashboard').then(m => ({ default: m.Dashboard })));
const HallOfFame = lazy(() => import('./components/HallOfFame').then(m => ({ default: m.HallOfFame })));
const Certificate = lazy(() => import('./components/Certificate').then(m => ({ default: m.Certificate })));
const AdminPanel = lazy(() => import('./components/AdminPanel').then(m => ({ default: m.AdminPanel })));
const AuthScreen = lazy(() => import('./components/AuthScreenV2').then(m => ({ default: m.AuthScreenV2 })));
const AuthCallback = lazy(() => import('./components/AuthCallback'));
const ProfileView = lazy(() => import('./components/ProfileView').then(m => ({ default: m.ProfileView })));
const EbooksView = lazy(() => import('./components/EbooksView').then(m => ({ default: m.EbooksView })));
const SatsangAudiosView = lazy(() => import('./components/SatsangAudiosView').then(m => ({ default: m.SatsangAudiosView })));
// Helper to resolve audio source from URL or base64
const resolveAudioSrc = (audio: SatsangAudio): string => {
  if (audio.audioUrl) {
    const API = API_URL;
    return audio.audioUrl.startsWith('http') ? audio.audioUrl : `${API}${audio.audioUrl}`;
  }
  return audio.audioData || '';
};
const AISantVani = lazy(() => import('./components/AISantVani').then(m => ({ default: m.AISantVani })));
const AboutUsView = lazy(() => import('./components/AboutUsView').then(m => ({ default: m.AboutUsView })));
const ContactUsView = lazy(() => import('./components/ContactUsView').then(m => ({ default: m.ContactUsView })));
const BlogsView = lazy(() => import('./components/BlogsView').then(m => ({ default: m.BlogsView })));
const ShlokaOfTheDayPage = lazy(() => import('./components/ShlokaOfTheDay').then(m => ({ default: m.ShlokaOfTheDayPage })));
const SadhakSelfTest = lazy(() => import('./components/SadhakSelfTest').then(m => ({ default: m.SadhakSelfTest })));
const AdminSadhakTest = lazy(() => import('./components/AdminSadhakTest').then(m => ({ default: m.AdminSadhakTest })));
const NotesView = lazy(() => import('./components/NotesView').then(m => ({ default: m.NotesView })));
const AlbumGallery = lazy(() => import('./components/AlbumGallery').then(m => ({ default: m.AlbumGallery })));
const AdminAlbumPanel = lazy(() => import('./components/AdminAlbumPanel').then(m => ({ default: m.AdminAlbumPanel })));
const FavoritesView = lazy(() => import('./components/FavoritesView').then(m => ({ default: m.FavoritesView })));
const PustikaRegistration = lazy(() => import('./components/PustikaRegistration').then(m => ({ default: m.PustikaRegistration })));
const AdminPustikaPanel = lazy(() => import('./components/AdminPustikaPanel').then(m => ({ default: m.AdminPustikaPanel })));
const PasswordReset = lazy(() => import('./components/PasswordReset').then(m => ({ default: m.PasswordReset })));
const VideosView = lazy(() => import('./components/VideosView').then(m => ({ default: m.VideosView })));
const AdminVideosPanel = lazy(() => import('./components/admin/AdminVideosPanel').then(m => ({ default: m.AdminVideosPanel })));
const PoemsListView = lazy(() => import('./components/PoemsListView').then(m => ({ default: m.PoemsListView })));
const PoemFullPageView = lazy(() => import('./components/PoemFullPageView').then(m => ({ default: m.PoemFullPageView })));
const LeelaChintan = lazy(() => import('./components/LeelaChintan'));
const TextReader = lazy(() => import('./components/TextReader'));
const SarvatraDarshan = lazy(() => import('./components/SarvatraDarshan'));
const HomeScreen = lazy(() => import('./components/HomeScreen'));

// Loading fallback component with skeleton
const PageLoader = () => (
  <div className="flex flex-col items-center justify-center h-64 animate-fade-in gap-4" data-testid="page-loader">
    <div className="flex flex-col items-center gap-3">
      <Loader2 size={32} className="animate-spin text-saffron" />
      <p className="text-sm text-gray-500 dark:text-stone-400 font-medium">लोड हो रहा है...</p>
    </div>
  </div>
);

// Check for special routes
const getSpecialRoute = () => {
  const path = window.location.pathname;
  if (path === '/reset-password' || path === '/reset') return 'reset-password';
  if (path === '/auth/callback') return 'auth-callback';
  return null;
};

const AppContent: React.FC = () => {
  // Schedule native alarms on app startup (Ekadashi + Shlok)
  const scheduleNativeStartupAlarms = async () => {
    try {
      // Ekadashi: Schedule reminders 1 day before each upcoming Ekadashi (at 8 PM)
      const now = new Date();
      const timestamps: number[] = [];
      for (const ek of EKADASHI_DATES) {
        const ekDate = new Date(ek.date);
        // 1 day before, at 8 PM
        const reminderDate = new Date(ekDate);
        reminderDate.setDate(reminderDate.getDate() - 1);
        reminderDate.setHours(20, 0, 0, 0);
        if (reminderDate > now) {
          timestamps.push(reminderDate.getTime());
        }
      }
      if (timestamps.length > 0) {
        await scheduleNativeEkadashi(timestamps.slice(0, 10)); // Next 10 Ekadashis
      }

      // Shlok of the Day: Daily at 7 AM
      await scheduleNativeShlok(7, 0);
    } catch (err) {
      console.log('[Startup] Native alarm scheduling skipped:', err);
    }
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>(() => {
    // sessionStorage clears on force close / full app kill
    // If no session flag, this is a fresh start → go HOME
    const isResumedSession = sessionStorage.getItem('golok_session_active');
    const savedTab = localStorage.getItem('golok_active_tab');
    
    if (!isResumedSession) {
      // Fresh start after force close → HOME
      sessionStorage.setItem('golok_session_active', '1');
      localStorage.setItem('golok_active_tab', 'HOME');
      return Tab.HOME;
    }
    
    // Force HOME for users who had JAAP as default (migration to new HOME tab)
    if (savedTab === 'JAAP') {
      localStorage.setItem('golok_active_tab', 'HOME');
      return Tab.HOME;
    }
    if (savedTab && Object.values(Tab).includes(savedTab as Tab)) {
      if (savedTab === Tab.POEM_DETAIL) return Tab.POEMS;
      return savedTab as Tab;
    }
    return Tab.HOME;
  });
  const [tabHistory, setTabHistory] = useState<Tab[]>(() => {
    const isResumedSession = sessionStorage.getItem('golok_session_active');
    if (!isResumedSession) return [Tab.HOME];
    const savedTab = localStorage.getItem('golok_active_tab');
    if (savedTab === 'JAAP' || !savedTab) return [Tab.HOME];
    if (savedTab && Object.values(Tab).includes(savedTab as Tab)) {
      if (savedTab === Tab.POEM_DETAIL) return [Tab.POEMS];
      return [savedTab as Tab];
    }
    return [Tab.HOME];
  });
  const [user, setUser] = useState<UserProfile | null>(null);
  const [branding, setBranding] = useState<BrandingConfig>({ appName: '', subTitle: '', logoUrl: '/logo.png' });
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSplash, setShowSplash] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0); 
  const [selectedAudioCategory, setSelectedAudioCategory] = useState<string | null>(null);
  const [selectedEbookCategory, setSelectedEbookCategory] = useState<string | null>(null);
  const [isReminderOpen, setIsReminderOpen] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [showShlokaModal, setShowShlokaModal] = useState(false);
  const [specialRoute] = useState(getSpecialRoute);
  const [showEkadashiReminder, setShowEkadashiReminder] = useState(false);
  const [showEkadashiModal, setShowEkadashiModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showBadgesModal, setShowBadgesModal] = useState(false);
  const [showCommunityModal, setShowCommunityModal] = useState(false);
  const [showRemindersModal, setShowRemindersModal] = useState(false);
  const [selectedAlbumCategory, setSelectedAlbumCategory] = useState<string | null>(null);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  
  // Poems State
  const [selectedPoem, setSelectedPoem] = useState<any>(null);
  const [poemsList, setPoemsList] = useState<any[]>([]);
  const [poemCategories, setPoemCategories] = useState<any[]>([]);
  
  // Check if we're processing Google OAuth callback
  const [oauthSessionId] = useState(() => {
    const hash = window.location.hash || '';
    const search = window.location.search || '';
    const href = window.location.href || '';
    
    let sessionId = '';
    // Try hash first (#session_id=xxx)
    if (hash.includes('session_id=')) {
      sessionId = hash.split('session_id=')[1]?.split('&')[0] || '';
    }
    // Try query params (?session_id=xxx)
    if (!sessionId && search.includes('session_id=')) {
      const params = new URLSearchParams(search);
      sessionId = params.get('session_id') || '';
    }
    // Try full URL regex
    if (!sessionId) {
      const match = href.match(/session_id=([^&&#]+)/);
      if (match) sessionId = match[1];
    }
    return sessionId;
  });
  
  const [isProcessingOAuth, setIsProcessingOAuth] = useState(() => {
    if (!oauthSessionId) return false;
    // Don't re-process if already handled
    if (localStorage.getItem('google_auth_processed')) {
      window.history.replaceState(null, '', window.location.pathname);
      return false;
    }
    return true;
  });
  
  // Notification State
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  
  // Snooze Modal State
  const [showSnoozeModal, setShowSnoozeModal] = useState(false);
  const [snoozeNotificationData, setSnoozeNotificationData] = useState({ title: '', body: '' });
  
  const { t, language, setLanguage } = useLanguage();

  // Audio State
  const [currentAudio, setCurrentAudio] = useState<SatsangAudio | null>(null);
  const [audioPlaylist, setAudioPlaylist] = useState<SatsangAudio[]>([]);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [playerView, setPlayerView] = useState<'hidden' | 'minimized' | 'fullscreen'>('hidden');
  const [audioProgress, setAudioProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Native audio (Capacitor Android — Media3 ExoPlayer)
  const [isNative] = useState(() => isNativeAudioAvailable());
  const [nativeCurrentTime, setNativeCurrentTime] = useState(0);
  const [nativeDuration, setNativeDuration] = useState(0);
  const nativeAudioEndedRef = useRef(false);

  // Decide: use native ExoPlayer (only for HTTP URLs) or HTML5 <audio> (base64 / web)
  const currentAudioSrc = currentAudio ? resolveAudioSrc(currentAudio) : '';
  const useNativePlayer = isNative && currentAudioSrc.startsWith('http');
  
  // App Tour
  const { shouldShowTour, triggerTour, closeTour } = useAppTour();
  const [showTour, setShowTour] = useState(false);
  
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem('ram_naam_theme') === 'dark';
  });

  // Navigate to a specific tab from notification tap
  const handleNotificationNavigation = useCallback((action: string) => {
    const actionTabMap: Record<string, Tab> = {
      'open_shlok': Tab.SHLOKA_OF_DAY,
      'open_jaap': Tab.JAAP,
      'open_ekadashi': Tab.HOME,
      'open_stats': Tab.DASHBOARD,
      'open_leaderboard': Tab.HALL_OF_FAME,
      'open_sangh': Tab.AKHAND_JAAP,
      'open_nidhi': Tab.NIDHI_OCR,
      'open_ebooks': Tab.EBOOKS,
      'open_videos': Tab.VIDEOS,
      'open_audios': Tab.SATSANG_AUDIOS,
      'open_blogs': Tab.BLOGS,
      'open_home': Tab.HOME,
    };
    const tab = actionTabMap[action];
    if (tab) {
      setTabHistory(prev => [...prev, tab]);
      setActiveTab(tab);
      localStorage.setItem('golok_active_tab', tab);
    }
  }, []);

  // Setup foreground push notification listeners for both Capacitor and PWA
  const setupPushNotificationListeners = useCallback(() => {
    try {
      const { Capacitor } = require('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        import('@capacitor/push-notifications').then(({ PushNotifications }) => {
          // Foreground: show toast when notification arrives while app is open
          PushNotifications.addListener('pushNotificationReceived', (notification: any) => {
            const title = notification.title || notification.data?.title || '';
            const body = notification.body || notification.data?.body || '';
            const action = notification.data?.action || '';
            
            toast(title, {
              description: body,
              duration: 6000,
              action: action ? {
                label: 'देखें',
                onClick: () => handleNotificationNavigation(action),
              } : undefined,
            });
          });

          // Tap: navigate when user taps the notification
          PushNotifications.addListener('pushNotificationActionPerformed', (event: any) => {
            const action = event.notification?.data?.action || '';
            if (action) {
              handleNotificationNavigation(action);
            }
          });
        }).catch(() => {});
      }
    } catch {
      // Not native — for PWA, listen to SW broadcast
    }

    // PWA: listen for service worker push event forwarded messages
    const handlePushMessage = (event: MessageEvent) => {
      if (event.data?.type === 'PUSH_NOTIFICATION_RECEIVED') {
        const { title, body, action } = event.data;
        toast(title || '🙏 नई सूचना', {
          description: body || '',
          duration: 6000,
          action: action ? {
            label: 'देखें',
            onClick: () => handleNotificationNavigation(action),
          } : undefined,
        });
      }
      if (event.data?.type === 'NOTIFICATION_TAP_ACTION') {
        const { action } = event.data;
        if (action) handleNotificationNavigation(action);
      }
    };
    navigator.serviceWorker?.addEventListener('message', handlePushMessage);
  }, [handleNotificationNavigation]);

  useEffect(() => {
    const initialize = async () => {
      // Check for OTA updates in background (Capacitor only)
      checkForUpdates();

      // Initialize notification channels for all mantras (Capacitor Android only)
      initAllNotificationChannels();
      setupDisableGuard();

      // Schedule native alarms (Ekadashi + Shlok of the Day)
      scheduleNativeStartupAlarms();

      // Configure StatusBar for Capacitor native app
      try {
        const { Capacitor } = require('@capacitor/core');
        if (Capacitor.isNativePlatform()) {
          const { StatusBar, Style } = require('@capacitor/status-bar');
          StatusBar.setBackgroundColor({ color: '#8B4513' });
          StatusBar.setStyle({ style: Style.Dark });
          StatusBar.setOverlaysWebView({ overlay: false });

          // Handle hardware back button in Capacitor
          const { App: CapApp } = require('@capacitor/app');
          CapApp.addListener('backButton', ({ canGoBack }: { canGoBack: boolean }) => {
            if (canGoBack) {
              window.history.back();
            } else {
              CapApp.minimizeApp();
            }
          });
        }
      } catch (e) { /* not native */ }

      // Setup push notification foreground listener (Capacitor + PWA)
      setupPushNotificationListeners();

      // Listen for native notification tap navigation (from MainActivity)
      const handleNativeTap = (e: Event) => {
        const action = (e as CustomEvent).detail?.action;
        if (action) handleNotificationNavigation(action);
      };
      window.addEventListener('notificationTap', handleNativeTap);

      // Load branding immediately from localStorage (fast)
      const brandingConfig = getBranding();
      const contact = getContactInfo();
      const notifs = getNotifications();
      
      setBranding(brandingConfig);
      setContactInfo(contact);
      setNotifications(notifs);
      
      // Then fetch latest from server (async update)
      fetchBrandingFromServer().then(serverBranding => {
        setBranding(serverBranding);
      }).catch(() => {});
      
      fetchContactInfoFromServer().then(serverContact => {
        setContactInfo(serverContact);
      }).catch(() => {});
      
      // Check for guest session first (instant from localStorage)
      const guestSessionStr = localStorage.getItem('ram_naam_guest_session');
      if (guestSessionStr) {
        try {
          const guestUser = JSON.parse(guestSessionStr) as UserProfile;
          setUser(guestUser);
          if (guestUser.language) {
            setLanguage(guestUser.language);
          }
          setLoading(false);
          return;
        } catch {
          // Invalid guest session, clear it
          localStorage.removeItem('ram_naam_guest_session');
        }
      }
      
      // Quick check: try to get cached user first (instant from localStorage)
      const cachedUser = getCachedUser();
      if (cachedUser) {
        // For non-guest users, verify session is still valid BEFORE showing logged-in state
        const sessionToken = localStorage.getItem('session_token');
        if (!cachedUser.isGuest && sessionToken) {
          try {
            const verifyUrl = API_URL;
            const verifyRes = await fetch(`${verifyUrl}/api/user/profile`, {
              headers: { 
                'Authorization': `Bearer ${sessionToken}`,
                'X-Session-Token': sessionToken
              }
            });
            if (verifyRes.status === 401) {
              // Session expired - clear stale data and force re-login
              console.warn('Session expired. Forcing re-login.');
              localStorage.removeItem('session_token');
              localStorage.removeItem('ram_naam_session_user');
              localStorage.removeItem('ram_naam_user_cache');
              setLoading(false);
              return; // Will show login screen since user is null
            }
          } catch {
            // Network error - allow offline use with cached data
          }
        }
        
        setUser(cachedUser);
        if (cachedUser.language) {
          setLanguage(cachedUser.language);
        }
        setLoading(false);
        
        // Verify and update from IndexedDB in background
        getCurrentUser().then(freshUser => {
          if (freshUser) {
            setUser(freshUser);
          } else if (!cachedUser.isGuest) {
            // Session expired on server - force re-login
            console.warn('Session invalid, clearing cached user');
            setUser(null);
          }
        });
        return;
      }
      
      // No cache - check if there's a session
      const sessionUsername = localStorage.getItem('ram_naam_session_user');
      if (!sessionUsername) {
        setLoading(false);
        return;
      }
      
      // Has session but no cache - fetch from IndexedDB
      const loggedUser = await getCurrentUser();
      setUser(loggedUser);
      
      if (loggedUser?.language) {
        setLanguage(loggedUser.language);
      }
      
      setLoading(false);
    };
    initialize();
  }, []);

  // Start reminder checker
  useEffect(() => {
    const stopFn = startReminderChecker();
    return () => stopFn();
  }, []);
  
  // Listen for snooze messages from service worker
  useEffect(() => {
    // Check URL params for snooze
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('snooze') === 'true') {
      setSnoozeNotificationData({
        title: urlParams.get('title') || '🔔 साधना का समय',
        body: urlParams.get('body') || 'राम नाम जपने का समय हो गया है 🙏'
      });
      setShowSnoozeModal(true);
      // Clear URL params
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    // Listen for service worker messages
    const handleSWMessage = (event: MessageEvent) => {
      if (event.data?.type === 'OPEN_SNOOZE_MODAL') {
        setSnoozeNotificationData({
          title: event.data.title || '🔔 साधना का समय',
          body: event.data.body || 'राम नाम जपने का समय हो गया है 🙏'
        });
        setShowSnoozeModal(true);
      }
    };
    
    navigator.serviceWorker?.addEventListener('message', handleSWMessage);
    
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
    };
  }, []);
  
  // PWA Install Tracking - SINGLE location with proper reset on uninstall
  const pwaTrackingDone = useRef(false);
  useEffect(() => {
    const trackPWAInstall = () => {
      // Strict check - only track once per install cycle
      if (pwaTrackingDone.current) {
        console.log('PWA tracking already done this cycle, skipping');
        return;
      }
      pwaTrackingDone.current = true;
      
          fetch(`${API_URL}/api/analytics/pwa-install`, { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          timestamp: new Date().toISOString(),
          platform: navigator.platform || 'unknown'
        })
      }).then(res => {
        console.log('PWA install tracked successfully:', res.ok);
      }).catch(err => {
        console.error('PWA tracking failed:', err);
      });
      
      // Reset tour flags so tour shows again for fresh install
      localStorage.removeItem('app_tour_completed');
      localStorage.removeItem('app_tour_first_session');
      localStorage.removeItem('app_first_open_done');
      console.log('Tour flags reset for fresh install');
    };
    
    // Reset tracking flag when app becomes installable again (after uninstall)
    const resetTrackingFlag = () => {
      console.log('App installable again - resetting tracking flag');
      pwaTrackingDone.current = false;
      
      // Also reset tour flags so next install shows tour
      localStorage.removeItem('app_tour_completed');
      localStorage.removeItem('app_tour_first_session');
      localStorage.removeItem('app_first_open_done');
    };

    window.addEventListener('appinstalled', trackPWAInstall);
    window.addEventListener('beforeinstallprompt', resetTrackingFlag);
    
    return () => {
      window.removeEventListener('appinstalled', trackPWAInstall);
      window.removeEventListener('beforeinstallprompt', resetTrackingFlag);
    };
  }, []);
  
  // Handle URL shortcuts (from PWA shortcuts)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    
    if (action === 'jaap') {
      setActiveTab(Tab.JAAP);
    } else if (action === 'goal') {
      // Will show personalized dashboard which is on JAAP tab
      setActiveTab(Tab.JAAP);
    } else if (action === 'community') {
      setShowCommunityModal(true);
    }
    
    // Clear the URL params after handling
    if (action) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Fetch poems data on initial load if POEMS tab is active
  useEffect(() => {
    const savedTab = localStorage.getItem('golok_active_tab');
    if (savedTab === Tab.POEMS || savedTab === Tab.POEM_DETAIL) {
      fetchPoemsData();
    }
  }, []);

  // Audio progress tracking
  useEffect(() => {
    if (useNativePlayer) return; // Native progress via polling below
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      const cur = audio.currentTime;
      const dur = audio.duration;
      if (dur) setAudioProgress((cur / dur) * 100);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    return () => audio.removeEventListener('timeupdate', handleTimeUpdate);
  }, [currentAudio, useNativePlayer]); // Re-run when currentAudio changes

  // Native audio polling (Media3 ExoPlayer state) — ONLY for progress/duration
  // DO NOT sync isAudioPlaying from native state — React is source of truth for UI
  useEffect(() => {
    if (!useNativePlayer || !currentAudio) return;
    const interval = setInterval(() => {
      const state = nativeGetAudioState();
      if (!state) return;
      if (state.duration > 0) {
        setAudioProgress((state.position / state.duration) * 100);
        setNativeCurrentTime(state.position / 1000); // ms → seconds
        setNativeDuration(state.duration / 1000);
      }
      // Detect audio ended → play next track
      if (state.completed && !nativeAudioEndedRef.current) {
        nativeAudioEndedRef.current = true;
        const idx = audioPlaylist.findIndex(a => a.id === currentAudio?.id);
        if (idx !== -1 && audioPlaylist.length > 1) {
          const nextIdx = (idx + 1) % audioPlaylist.length;
          setCurrentAudio(audioPlaylist[nextIdx]);
        } else {
          setIsAudioPlaying(false);
        }
      }
      if (!state.completed) {
        nativeAudioEndedRef.current = false;
      }
    }, 500);
    return () => clearInterval(interval);
  }, [useNativePlayer, currentAudio?.id, audioPlaylist]);

  // Trigger App Tour - only after onboarding is complete (all welcome modals done)
  useEffect(() => {
    // Wait for onboarding to complete before showing tour
    if (!onboardingComplete) return;
    
    // Don't show tour if any modal is currently open
    if (showWelcomeModal || showShlokaModal || showEkadashiModal) return;
    
    if (shouldShowTour && !showTour) {
      const timer = setTimeout(() => {
        setShowTour(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [shouldShowTour, showTour, onboardingComplete, showWelcomeModal, showShlokaModal, showEkadashiModal]);

  // Native auto-play: ONLY when track changes (not on isAudioPlaying change!)
  useEffect(() => {
    if (!useNativePlayer || !currentAudio) return;
    const url = resolveAudioSrc(currentAudio);
    console.log('[NativeAudio] Starting:', currentAudio.title, url);
    nativePlayAudio(url, currentAudio.title, currentAudio.speaker || '');
    setIsAudioPlaying(true);
    nativeAudioEndedRef.current = false;
  }, [currentAudio?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Web auto-play when currentAudio changes AND audio element is ready
  useEffect(() => {
    if (useNativePlayer) return; // Native handled above

    // Web: use HTML5 <audio> element
    const audio = audioRef.current;
    if (!audio || !currentAudio) return;
    
    const handleCanPlay = () => {
      if (isAudioPlaying) {
        audio.play().catch(e => {
          console.error('Auto-play failed:', e);
          setIsAudioPlaying(false);
        });
      }
    };
    
    // If audio is already ready, play immediately
    if (audio.readyState >= 3 && isAudioPlaying) {
      audio.play().catch(e => {
        console.error('Auto-play failed:', e);
        setIsAudioPlaying(false);
      });
    } else {
      // Otherwise wait for canplay event
      audio.addEventListener('canplay', handleCanPlay, { once: true });
    }
    
    return () => {
      audio.removeEventListener('canplay', handleCanPlay);
    };
  }, [currentAudio?.id, isAudioPlaying]);

  // Refs to track current state for back button handler
  const playerViewRef = useRef(playerView);
  const currentAudioRef = useRef(currentAudio);
  const tabHistoryRef = useRef(tabHistory);
  
  // Keep refs in sync with state
  useEffect(() => {
    playerViewRef.current = playerView;
  }, [playerView]);
  
  useEffect(() => {
    currentAudioRef.current = currentAudio;
  }, [currentAudio]);
  
  useEffect(() => {
    tabHistoryRef.current = tabHistory;
  }, [tabHistory]);

  // Note: Android back button handling deferred to native app conversion
  // pushState approach doesn't work reliably in PWA standalone mode on all Android devices

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('ram_naam_theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('ram_naam_theme', 'light');
    }
  }, [isDarkMode]);

  const handleNavigate = (tab: Tab) => {
    if (tab === activeTab) return;
    // Auto-minimize player when navigating away
    if (playerView === 'fullscreen' && currentAudio) {
      setPlayerView('minimized');
    }
    setTabHistory(prev => [...prev, tab]);
    setActiveTab(tab);
    localStorage.setItem('golok_active_tab', tab);
    if (tab !== Tab.SATSANG_AUDIOS) setSelectedAudioCategory(null);
    
    // Push history state for back button handling
    window.history.pushState({ tab: tab }, '', window.location.pathname);
    
    // Fetch poems data when navigating to poems section
    if (tab === Tab.POEMS || tab === Tab.POEM_DETAIL) {
      fetchPoemsData();
    }
  };

  // Handle browser/Android back button at tab level
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // If the video is open in full-screen, minimize it into the floating
      // in-app mini player instead of exiting the view (YouTube-style).
      if (videoMiniStore.get().view === 'fullscreen') {
        videoMiniStore.setView('minimized');
        return;
      }
      // If fullscreen player is open, minimize it
      if (playerView === 'fullscreen' && currentAudio) {
        setPlayerView('minimized');
        return;
      }
      
      // If there's tab history, go back
      if (tabHistory.length > 1) {
        const newHistory = tabHistory.slice(0, -1);
        setTabHistory(newHistory);
        const previousTab = newHistory[newHistory.length - 1];
        setActiveTab(previousTab);
        localStorage.setItem('golok_active_tab', previousTab);
      } else {
        // On home tab, push a state to prevent app from closing
        if (activeTab === Tab.JAAP) {
          window.history.pushState({ tab: Tab.JAAP }, '', window.location.pathname);
        } else {
          // Go to home
          setActiveTab(Tab.JAAP);
          setTabHistory([Tab.JAAP]);
          localStorage.setItem('golok_active_tab', Tab.JAAP);
        }
      }
    };
    
    // Initialize history state
    if (!window.history.state?.tab) {
      window.history.replaceState({ tab: activeTab }, '', window.location.pathname);
    }
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [tabHistory, activeTab, playerView, currentAudio]);

  // When mini-player's expand button fires, switch to the Videos tab so the
  // underlying screen matches the fullscreen video (avoids "video on top of
  // random page" — e.g. JAAP/Home — when expanding from elsewhere).
  useEffect(() => {
    const handler = () => {
      if (activeTab !== Tab.VIDEOS) {
        setTabHistory(prev => [...prev, Tab.VIDEOS]);
        setActiveTab(Tab.VIDEOS);
        localStorage.setItem('golok_active_tab', Tab.VIDEOS);
      }
    };
    window.addEventListener('video-expand-navigate', handler);
    return () => window.removeEventListener('video-expand-navigate', handler);
  }, [activeTab]);

  const fetchPoemsData = async () => {
      try {
      const [poemsRes, categoriesRes] = await Promise.all([
        fetch(`${API_URL}/api/poems?limit=100`),
        fetch(`${API_URL}/api/poems/categories`)
      ]);
      
      if (poemsRes.ok) {
        const data = await poemsRes.json();
        setPoemsList(data.poems || []);
      }
      
      if (categoriesRes.ok) {
        const data = await categoriesRes.json();
        setPoemCategories(data);
      }
    } catch (err) {
      console.error('Error fetching poems data:', err);
    }
  };

  const handleBack = () => {
    // If fullscreen player is open, minimize it instead of going back
    if (playerView === 'fullscreen' && currentAudio) {
      setPlayerView('minimized');
      return;
    }
    if (tabHistory.length <= 1) return;
    const newHistory = tabHistory.slice(0, -1);
    setTabHistory(newHistory);
    const previousTab = newHistory[newHistory.length - 1];
    setActiveTab(previousTab);
    // Save to localStorage to persist across page refreshes
    localStorage.setItem('golok_active_tab', previousTab);
  };

  const handleUpdateUser = async (updatedUser: UserProfile) => {
    setUser(updatedUser);
    await saveUserData(updatedUser);
  };

  const handleLoginSuccess = async (loggedInUser: UserProfile) => {
    setUser(loggedInUser);
    if (loggedInUser.language) setLanguage(loggedInUser.language);
    
    // Persist user data for session restoration
    try {
      const { syncPasswordUser, cacheUser } = await import('./services/mockService');
      if (loggedInUser.email) {
        await syncPasswordUser(loggedInUser.email, loggedInUser.displayName || loggedInUser.username || '', loggedInUser);
      } else {
        cacheUser(loggedInUser);
      }
    } catch (e) {
      console.error('Failed to persist user data:', e);
    }
    
    // Check if this is the user's first time (show welcome modal) - per user basis
    const welcomeKey = `ram_naam_welcome_shown_${loggedInUser.id}`;
    const hasSeenWelcome = localStorage.getItem(welcomeKey);
    if (!hasSeenWelcome) {
      setTimeout(() => {
        setShowWelcomeModal(true);
        localStorage.setItem(welcomeKey, 'true');
      }, 800); // Show welcome modal first
      
      // Show Shloka modal after welcome modal is closed
      // The welcome modal will handle showing shloka modal on close
      return;
    }
    
    // Returning user - mark onboarding complete so tour doesn't interfere
    setOnboardingComplete(true);
    
    // Show Shloka of the Day modal once per day after login (for returning users)
    const lastShown = localStorage.getItem('shloka_modal_last_shown');
    const today = new Date().toDateString();
    if (lastShown !== today) {
      setTimeout(() => {
        setShowShlokaModal(true);
        localStorage.setItem('shloka_modal_last_shown', today);
      }, 1500); // Show after 1.5 seconds
    }
    
    // Show Ekadashi reminder after Shloka modal (if applicable)
    setTimeout(() => {
      setShowEkadashiReminder(true);
    }, 3500); // Show after 3.5 seconds (after shloka modal)
  };
  
  // Handle welcome modal close - show shloka modal after, then trigger tour
  const handleWelcomeModalClose = () => {
    setShowWelcomeModal(false);
    
    // Show Shloka modal after welcome modal closes (only if not already shown today)
    const today = new Date().toDateString();
    const lastShown = localStorage.getItem('shloka_modal_last_shown');
    if (lastShown !== today) {
      setTimeout(() => {
        setShowShlokaModal(true);
        localStorage.setItem('shloka_modal_last_shown', today);
      }, 500);
    }
    
    // Show Ekadashi reminder after Shloka modal
    setTimeout(() => {
      setShowEkadashiReminder(true);
    }, 3000);
    
    // Mark onboarding complete - Tour will trigger after all modals
    setTimeout(() => {
      setOnboardingComplete(true);
    }, 4000); // After Shloka and Ekadashi modals
  };

  // Handle Google OAuth callback success
  const handleGoogleAuthSuccess = async (userData: { user_id: string; email: string; name: string; picture?: string; session_token?: string; total_count?: number; mantra_counts?: any; likhit_counts?: any; smaran_counts?: any; vani_counts?: any; nidhi_counts?: any; streak_days?: number }) => {
    try {
      // Save session token to localStorage for authenticated API calls
      if (userData.session_token) {
        localStorage.setItem('session_token', userData.session_token);
      }
      
      // Mark Google auth as processed (prevents re-processing on reload)
      localStorage.setItem('google_auth_processed', 'true');
      // Clean URL hash/params
      window.history.replaceState(null, '', window.location.pathname);
      
      // Pass backend counts to syncGoogleUser for merge
      const backendCounts = {
        total_count: userData.total_count,
        mantra_counts: userData.mantra_counts,
        likhit_counts: userData.likhit_counts,
        smaran_counts: userData.smaran_counts,
        vani_counts: userData.vani_counts,
        nidhi_counts: userData.nidhi_counts,
        streak_days: userData.streak_days,
      };
      const localUser = await syncGoogleUser(userData.email, userData.name, userData.picture || '', backendCounts);
      setUser(localUser);
      setIsProcessingOAuth(false);
      setAuthError(null);
    } catch (error: any) {
      console.error('Error syncing Google user:', error);
      setAuthError('Failed to complete sign in. Please try again.');
      setIsProcessingOAuth(false);
    }
  };

  // Handle Google OAuth callback error
  const handleGoogleAuthError = (error: string) => {
    setAuthError(error);
    setIsProcessingOAuth(false);
  };

  const handleLogout = () => {
    // Clear local state FIRST (instant, don't wait for API)
    logoutUser();
    localStorage.removeItem('ram_naam_guest_session');
    localStorage.removeItem('golok_active_tab');
    localStorage.removeItem('session_token');
    localStorage.removeItem('google_auth_processed');
    setUser(null);
    setActiveTab(Tab.JAAP);
    setTabHistory([Tab.JAAP]);
    setCurrentAudio(null);
    setIsAudioPlaying(false);
    if (useNativePlayer) nativeStopAudio();
    const apiUrl = API_URL;
    const token = localStorage.getItem('session_token');
    fetch(`${apiUrl}/api/auth/logout`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      credentials: 'include',
    }).catch(() => {});
  };

  const handleRefresh = async () => {
    const brandingConfig = getBranding();
    const contact = getContactInfo();
    const notifs = getNotifications();
    setBranding(brandingConfig);
    setContactInfo(contact);
    setNotifications(notifs);
    
    // For guest users, preserve the guest session (don't fetch from IndexedDB)
    if (user?.isGuest) {
      // Guest session stays as-is, just refresh other data
      setRefreshKey(prev => prev + 1);
      return;
    }
    
    // For regular users, refresh from IndexedDB
    const loggedUser = await getCurrentUser();
    setUser(loggedUser);
    setRefreshKey(prev => prev + 1); 
  };

  const handleUpdateBranding = (config: BrandingConfig) => {
    setBranding(config);
  };

  // Notification Handlers
  const handleMarkRead = (id: string) => {
    markNotificationRead(id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const handleMarkAllRead = () => {
    markAllNotificationsRead();
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const handleSplashComplete = useCallback(() => {
    setShowSplash(false);
  }, []);

  if (loading || showSplash) {
     return (
       <>
         {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
         {!showSplash && loading && (
           <div className="min-h-screen bg-cream dark:bg-stone-950 flex flex-col items-center justify-center gap-6">
             <div className="relative">
               <div className="w-20 h-20 border-4 border-saffron/20 rounded-full"></div>
               <div className="absolute top-0 left-0 w-20 h-20 border-4 border-transparent border-t-saffron rounded-full animate-spin"></div>
             </div>
             <div className="text-center">
               <p className="text-templeRed dark:text-saffron font-bold text-xl">{t('common_radhe')}</p>
               <p className="text-gray-500 dark:text-stone-500 text-sm mt-1">Loading your divine experience...</p>
             </div>
           </div>
         )}
       </>
     );
  }

  // Handle Password Reset route
  if (specialRoute === 'reset-password') {
    return (
      <div className={isDarkMode ? 'dark' : ''}>
        <Suspense fallback={<PageLoader />}>
          <PasswordReset />
        </Suspense>
      </div>
    );
  }

  // Handle Google OAuth callback - process session_id BEFORE showing auth screen
  if (isProcessingOAuth) {
    return (
      <div className={isDarkMode ? 'dark' : ''}>
        <Suspense fallback={<PageLoader />}>
          <AuthCallback 
            sessionId={oauthSessionId}
            onAuthSuccess={handleGoogleAuthSuccess}
            onAuthError={handleGoogleAuthError}
          />
        </Suspense>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={isDarkMode ? 'dark' : ''}>
        <Suspense fallback={<PageLoader />}>
          <AuthScreen 
            onAuthSuccess={handleLoginSuccess} 
            branding={branding}
          />
        </Suspense>
        {authError && (
          <div className="fixed bottom-4 left-4 right-4 bg-red-500 text-white p-4 rounded-lg shadow-lg z-50">
            <p className="font-bold">Sign in failed</p>
            <p className="text-sm">{authError}</p>
            <button 
              onClick={() => setAuthError(null)}
              className="mt-2 text-sm underline"
            >
              Dismiss
            </button>
          </div>
        )}
      </div>
    );
  }

  const isAdmin = user.username === 'admin' || user.email?.startsWith('admin@') || user.email === 'admin@test.com' || user.email === 'admin@golok.com' || user.email === 'imsachinn@gmail.com' || user.email === 'golokstores@gmail.com';
  const whatsappNumber = contactInfo?.whatsapp || '919876543210'; 
  const unreadCount = notifications.filter(n => !n.read).length;
  const isLoggedIn = user && !user.isGuest;

  return (
    <FavoritesProvider userId={user?.id} isLoggedIn={isLoggedIn}>
    <div className={`h-[100dvh] font-sans text-gray-900 dark:text-stone-100 flex flex-col transition-colors duration-300 ${isDarkMode ? 'dark' : ''}`}
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>

      {/* DivineAlarmOverlay - ALWAYS mounted, outside scroll container */}
      <DivineAlarmOverlay />

      {/* DIAGNOSTIC BANNER removed — caching fix applied */}

      {/* SadhakSelfTest - Rendered OUTSIDE scroll container for proper fixed button */}
      {activeTab === Tab.SADHAK_TEST && (
        <div className="flex-1 overflow-hidden">
          <Suspense fallback={<PageLoader />}>
            <SadhakSelfTest user={user} onBack={() => handleNavigate(Tab.JAAP)} />
          </Suspense>
        </div>
      )}

      {/* Main scrollable content area - hidden when SadhakSelfTest is active */}
      <div className={`flex-1 overflow-y-auto overflow-x-hidden bg-cream dark:bg-stone-900 ${activeTab === Tab.SADHAK_TEST ? 'hidden' : ''}`}>
        
        {/* Global Components */}
        <DivineReminder isOpen={isReminderOpen} onClose={() => setIsReminderOpen(false)} isAdmin={isAdmin} />
        
        <NotificationCenter 
          isOpen={isNotificationOpen} 
          onClose={() => setIsNotificationOpen(false)}
          notifications={notifications}
          onMarkRead={handleMarkRead}
          onMarkAllRead={handleMarkAllRead}
        />
        
        {/* App Tour */}
        <AppTour
          isOpen={showTour}
          onClose={() => {
            setShowTour(false);
            closeTour();
          }}
          onOpenSidebar={() => setSidebarOpen(true)}
          onCloseSidebar={() => setSidebarOpen(false)}
          onNavigateHome={() => handleNavigate(Tab.JAAP)}
          language={language}
        />

        {/* Global Audio Element — renders for web AND base64 on native */}
        {currentAudio && !useNativePlayer && (
          <audio 
            ref={audioRef}
            src={resolveAudioSrc(currentAudio)}
            className="hidden"
          />
        )}

        {/* Full-Screen Player */}
        {currentAudio && playerView === 'fullscreen' && (
          <SatsangPlayerPage 
            currentAudio={currentAudio}
            playlist={audioPlaylist}
            isPlaying={isAudioPlaying}
            setIsPlaying={setIsAudioPlaying}
            onAudioChange={setCurrentAudio}
            onMinimize={() => setPlayerView('minimized')}
            onClose={() => {
              if (useNativePlayer) nativeStopAudio();
              setCurrentAudio(null);
              setIsAudioPlaying(false);
              setPlayerView('hidden');
            }}
            user={user}
            onUpdateUser={handleUpdateUser}
            audioRef={audioRef as React.RefObject<HTMLAudioElement>}
            onLogout={handleLogout}
            isNative={useNativePlayer}
            nativeProgress={audioProgress}
            nativeCurrentTime={nativeCurrentTime}
            nativeDuration={nativeDuration}
          />
        )}

        {/* Mini Player moved to layout flow — see below nav section */}

        <StoreSidebar 
          isOpen={sidebarOpen} 
          onClose={() => setSidebarOpen(false)} 
          onNavigate={handleNavigate}
          onNavigateAudio={(category) => {
            setSelectedAudioCategory(category);
            handleNavigate(Tab.SATSANG_AUDIOS);
          }}
          onNavigateEbook={(category) => {
            setSelectedEbookCategory(category);
            handleNavigate(Tab.EBOOKS);
          }}
          onNavigateAlbum={(category) => {
            setSelectedAlbumCategory(category);
            handleNavigate(Tab.ALBUM_GALLERY);
          }}
          onOpenEkadashi={() => setShowEkadashiModal(true)}
          onOpenBadges={() => setShowBadgesModal(true)}
          onOpenCommunity={() => setShowCommunityModal(true)}
          onOpenReminders={() => setShowRemindersModal(true)}
          isAdmin={isAdmin}
        />
        
        <Header 
          toggleSidebar={() => setSidebarOpen(!sidebarOpen)} 
          user={user}
          onOpenProfile={() => handleNavigate(Tab.PROFILE)}
          isDarkMode={isDarkMode}
          onToggleDarkMode={() => setIsDarkMode(!isDarkMode)}
          onOpenReminder={() => setIsReminderOpen(true)}
          onOpenSadhanaReminder={() => setShowRemindersModal(true)}
          branding={branding}
          language={language}
          onToggleLanguage={() => {
            const newLang = language === 'en' ? 'hi' : 'en';
            setLanguage(newLang);
            handleUpdateUser({ ...user, language: newLang });
          }}
          onBack={handleBack}
          showBack={tabHistory.length > 1}
          onGoHome={() => handleNavigate(Tab.JAAP)}
          onNavigate={handleNavigate}
          onOpenEkadashi={() => setShowEkadashiModal(true)}
          onOpenBadges={() => setShowBadgesModal(true)}
          onOpenCommunity={() => setShowCommunityModal(true)}
        />

        {/* Quick Access Bar - Sticky below header */}
        <QuickAccessBar activeTab={activeTab} onNavigate={handleNavigate} />

        <div className="w-full">
          <PullToRefresh onRefresh={handleRefresh}>
            <main className="w-full max-w-7xl mx-auto px-4 py-2 md:py-4">
              <Suspense fallback={<PageLoader />}>
                {activeTab === Tab.HOME && (
                  <HomeScreen user={user} onNavigate={handleNavigate} />
                )}
                {activeTab === Tab.JAAP && (
                  <>
                    {/* Main Jaap Interface - Priority */}
                    <JaapInterface 
                      user={user} 
                      onUpdateUser={async (updatedUser) => {
                        await handleUpdateUser(updatedUser);
                        if (updatedUser.total_count > (user?.total_count || 0)) {
                          const countDiff = updatedUser.total_count - (user?.total_count || 0);
                          updateCommunityContribution(updatedUser.id, countDiff);
                          const today = new Date().toISOString().split('T')[0];
                          saveDailyStats(updatedUser.id, {
                            date: today,
                            count: updatedUser.total_count,
                            likhit: (Object.values(updatedUser.likhit_counts || {}) as number[]).reduce((a, b) => a + b, 0),
                            smaran: (Object.values(updatedUser.smaran_counts || {}) as number[]).reduce((a, b) => a + b, 0),
                            vani: (Object.values(updatedUser.vani_counts || {}) as number[]).reduce((a, b) => a + b, 0),
                          });
                        }
                      }} 
                      onLogout={handleLogout} 
                    />
                  </>
                )}
                {activeTab === Tab.AKHAND_JAAP && <AkhandJaapRooms user={user} onUpdateUser={handleUpdateUser} onOpenLeaderboard={() => setShowCommunityModal(true)} />}
                {activeTab === Tab.SADHANA_INSIGHTS && (
                  <div className="space-y-4">
                    {/* Feature Cards */}
                    {user && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <button 
                          onClick={() => setShowBadgesModal(true)}
                          className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-2xl p-4 text-left hover:shadow-md transition-shadow"
                          data-testid="badge-showcase"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">🏆</span>
                            <div>
                              <p className="font-semibold text-gray-800 dark:text-gray-200">उपलब्धि बैज</p>
                              <p className="text-sm text-gray-500">Achievement Badges</p>
                            </div>
                          </div>
                        </button>
                        
                        <button 
                          onClick={() => setShowCommunityModal(true)}
                          className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-4 text-left hover:shadow-md transition-shadow"
                          data-testid="community-card"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">👥</span>
                            <div>
                              <p className="font-semibold text-gray-800 dark:text-gray-200">सामूहिक साधना</p>
                              <p className="text-sm text-gray-500">Community Challenges</p>
                            </div>
                          </div>
                        </button>
                        
                        <button 
                          onClick={() => setShowRemindersModal(true)}
                          className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-2xl p-4 text-left hover:shadow-md transition-shadow"
                          data-testid="reminder-card"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">🔔</span>
                            <div>
                              <p className="font-semibold text-gray-800 dark:text-gray-200">साधना रिमाइंडर</p>
                              <p className="text-sm text-gray-500">Daily Reminders</p>
                            </div>
                          </div>
                        </button>
                      </div>
                    )}
                    
                    {/* Personalized Dashboard */}
                    {user && !user.isGuest && (
                      <PersonalizedDashboard user={user} />
                    )}
                    
                    {/* Original Sadhana Insights */}
                    <SadhanaInsights user={user} />
                  </div>
                )}
                {activeTab === Tab.NIDHI_OCR && <NidhiOCR user={user} onUpdateUser={handleUpdateUser} onNavigate={handleNavigate} onLogout={handleLogout} />}
                {activeTab === Tab.DASHBOARD && <Dashboard user={user} />}
                {activeTab === Tab.HALL_OF_FAME && <HallOfFame key={`leaders-${refreshKey}`} />}
                {activeTab === Tab.CERTIFICATE && <Certificate user={user} />}
                {activeTab === Tab.EBOOKS && (
                  <EbooksView 
                    key={`ebooks-${refreshKey}-${selectedEbookCategory}`} 
                    initialCategory={selectedEbookCategory}
                    user={user}
                    onUpdateUser={handleUpdateUser}
                  />
                )}
                {activeTab === Tab.AI_SANT_VANI && <AISantVani />}
                {activeTab === Tab.SATSANG_AUDIOS && (
                  <SatsangAudiosView 
                    key={`audios-${refreshKey}-${selectedAudioCategory}`} 
                    initialCategory={selectedAudioCategory} 
                    currentAudio={currentAudio}
                    isPlaying={isAudioPlaying}
                    user={user}
                    onUpdateUser={handleUpdateUser}
                    onPlay={(audio, list) => {
                      if (currentAudio?.id === audio.id) {
                        // If same track, toggle play/pause or expand player
                        if (playerView === 'minimized') {
                          setPlayerView('fullscreen');
                          window.history.pushState({ player: 'fullscreen' }, '', window.location.pathname);
                        } else {
                          setIsAudioPlaying(!isAudioPlaying);
                        }
                      } else {
                        // New track - set state and play
                        setAudioPlaylist(list);
                        setCurrentAudio(audio);
                        setIsAudioPlaying(true);
                        setPlayerView('fullscreen');
                        window.history.pushState({ player: 'fullscreen' }, '', window.location.pathname);
                      }
                    }}
                  />
                )}
                {activeTab === Tab.PROFILE && <ProfileView user={user} onUpdateUser={handleUpdateUser} onLogout={handleLogout} />}
                {activeTab === Tab.ABOUT_US && <AboutUsView />}
                {activeTab === Tab.CONTACT_US && <ContactUsView />}
                {activeTab === Tab.SHLOKA_OF_DAY && <ShlokaOfTheDayPage />}
                {activeTab === Tab.BLOGS && <BlogsView user={user} onUpdateUser={handleUpdateUser} isAdmin={isAdmin} />}
                {/* SadhakSelfTest rendered outside scroll container - see above */}
                {activeTab === Tab.ADMIN_SADHAK_TEST && isAdmin && <AdminSadhakTest onBack={() => handleNavigate(Tab.ADMIN)} />}
                {activeTab === Tab.ADMIN && isAdmin && <AdminPanel onUpdateBranding={handleUpdateBranding} />}
                {activeTab === Tab.NOTES && <NotesView user={user} />}
                {activeTab === Tab.ALBUM_GALLERY && <AlbumGallery initialCategory={selectedAlbumCategory} user={user} />}
                {activeTab === Tab.ADMIN_ALBUM && isAdmin && <AdminAlbumPanel onBack={() => handleNavigate(Tab.ADMIN)} />}
                {activeTab === Tab.FAVORITES && (
                  <FavoritesView 
                    user={user} 
                    onNavigate={handleNavigate}
                    onPlayAudio={(audio, list) => {
                      setAudioPlaylist(list);
                      setCurrentAudio(audio);
                      setIsAudioPlaying(true);
                      setPlayerView('fullscreen');
                      window.history.pushState({ player: 'fullscreen' }, '', window.location.pathname);
                    }}
                  />
                )}
                {activeTab === Tab.PUSTIKA_REGISTER && <PustikaRegistration user={user} />}
                {activeTab === Tab.ADMIN_PUSTIKA && isAdmin && <AdminPustikaPanel onBack={() => handleNavigate(Tab.ADMIN)} />}
                {activeTab === Tab.VIDEOS && <VideosView user={user} onNavigate={handleNavigate} />}
                {activeTab === Tab.ADMIN_VIDEOS && <AdminVideosPanel onBack={() => handleNavigate(Tab.ADMIN)} />}
                {activeTab === Tab.POEMS && (
                  <PoemsListView 
                    onSelectPoem={(poem) => {
                      setSelectedPoem(poem);
                      handleNavigate(Tab.POEM_DETAIL);
                    }}
                  />
                )}
                {activeTab === Tab.POEM_DETAIL && selectedPoem && (
                  <PoemFullPageView
                    poem={selectedPoem}
                    poems={poemsList}
                    categories={poemCategories}
                    onBack={() => handleNavigate(Tab.POEMS)}
                    onNavigatePoem={(poem) => setSelectedPoem(poem)}
                  />
                )}
                {activeTab === Tab.LEELA_CHINTAN && <LeelaChintan user={user} onBack={() => handleNavigate(Tab.JAAP)} />}
                {activeTab === Tab.TEXT_READER && <TextReader />}
                {activeTab === Tab.SARVATRA_DARSHAN && <SarvatraDarshan onBack={() => handleNavigate(Tab.HOME)} />}
              </Suspense>
            </main>
          </PullToRefresh>

          <div className="pb-4">
             <Footer branding={branding} />
          </div>
        </div>

        {/* PWA Install Prompt */}
        <PWAInstallPrompt />

        {/* WhatsApp Float Button */}
        <a
          href={`https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-24 right-6 z-50 bg-[#25D366] hover:bg-[#20bd5a] text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center border-4 border-white dark:border-stone-800"
          aria-label="Chat on WhatsApp"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.72.94 3.659 1.437 5.63 1.438h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </a>
        
        {/* Shloka of the Day Modal */}
        <ShlokaModal isOpen={showShlokaModal} onClose={() => setShowShlokaModal(false)} />
        
        {/* Welcome Modal for first-time users */}
        <WelcomeModal 
          isOpen={showWelcomeModal} 
          onClose={handleWelcomeModalClose}
          userName={user?.displayName || user?.username}
        />
        
        {/* Push Notification Permission Prompt */}
        {user && !user.isGuest && (
          <NotificationPrompt 
            userId={user.id} 
            language={language}
          />
        )}
        
        {/* Achievement Badges Modal */}
        {user && (
          <AchievementBadges 
            user={user} 
            isOpen={showBadgesModal} 
            onClose={() => setShowBadgesModal(false)} 
          />
        )}
        
        {/* Community Leaderboard Modal */}
        {user && (
          <CommunityLeaderboard 
            user={user} 
            isOpen={showCommunityModal} 
            onClose={() => setShowCommunityModal(false)} 
          />
        )}
        
        {/* Daily Reminders Modal */}
        <DailyReminders 
          isOpen={showRemindersModal} 
          onClose={() => setShowRemindersModal(false)} 
        />
        
        {/* Snooze Modal */}
        <SnoozeModal
          isOpen={showSnoozeModal}
          onClose={() => setShowSnoozeModal(false)}
          notificationTitle={snoozeNotificationData.title}
          notificationBody={snoozeNotificationData.body}
        />
        
        {/* Ekadashi Reminder Modal - shows automatically based on settings */}
        {showEkadashiReminder && (
          <EkadashiReminder onClose={() => setShowEkadashiReminder(false)} />
        )}
        
        {/* Manual Ekadashi Modal - triggered from sidebar click */}
        {showEkadashiModal && (
          <EkadashiReminder onClose={() => setShowEkadashiModal(false)} forceShow={true} />
        )}
      </div>

      {/* Mini Player — in layout flow between content and nav, NOT fixed */}
      {currentAudio && playerView === 'minimized' && (
        <MiniPlayer 
          currentAudio={currentAudio}
          isPlaying={isAudioPlaying}
          setIsPlaying={setIsAudioPlaying}
          onExpand={() => {
            setPlayerView('fullscreen');
            window.history.pushState({ player: 'fullscreen' }, '', window.location.pathname);
          }}
          onClose={() => {
            if (useNativePlayer) nativeStopAudio();
            setCurrentAudio(null);
            setIsAudioPlaying(false);
            setPlayerView('hidden');
          }}
          progress={audioProgress}
          audioRef={audioRef as React.RefObject<HTMLAudioElement>}
          isNative={useNativePlayer}
        />
      )}

      {/* Persistent in-app video player (YouTube-style shared element)
          — single <video>/<iframe> whose position animates between
          full-screen and the floating mini card. Never remounts. */}
      <PersistentVideoPlayer />
      
      {/* Bottom Navigation - Using flexbox, NOT position:fixed */}
      <nav 
        className="flex-shrink-0 bg-white dark:bg-stone-800 border-t border-saffron/30 dark:border-saffron/20 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex justify-around items-center h-14 max-w-7xl mx-auto px-1">
          <button 
            onClick={() => handleNavigate(Tab.HOME)} 
            data-testid="nav-home"
            className={`flex flex-col items-center gap-0.5 w-full h-full justify-center transition-colors ${activeTab === Tab.HOME ? 'text-templeRed dark:text-saffron' : 'text-gray-400 dark:text-stone-500'}`}
          >
            <Home size={18} />
            <span className="text-[8px] md:text-[10px] font-bold uppercase tracking-tighter">{t('nav_home')}</span>
          </button>
          
          <button onClick={() => handleNavigate(Tab.JAAP)} className={`flex flex-col items-center gap-0.5 w-full h-full justify-center transition-colors ${activeTab === Tab.JAAP ? 'text-templeRed dark:text-saffron' : 'text-gray-400 dark:text-stone-500'}`}>
            <PenTool size={18} />
            <span className="text-[8px] md:text-[10px] font-bold uppercase tracking-tighter">{t('nav_jaap')}</span>
          </button>
          
          <button onClick={() => handleNavigate(Tab.AKHAND_JAAP)} className={`flex flex-col items-center gap-0.5 w-full h-full justify-center transition-colors ${activeTab === Tab.AKHAND_JAAP ? 'text-templeRed dark:text-saffron' : 'text-gray-400 dark:text-stone-500'}`}>
            <Users size={18} />
            <span className="text-[8px] md:text-[10px] font-bold uppercase tracking-tighter">{t('nav_sangha')}</span>
          </button>

          <button onClick={() => handleNavigate(Tab.NIDHI_OCR)} className={`flex flex-col items-center gap-0.5 w-full h-full justify-center transition-colors ${activeTab === Tab.NIDHI_OCR ? 'text-templeRed dark:text-saffron' : 'text-gray-400 dark:text-stone-500'}`}>
            <Scan size={18} className={activeTab === Tab.NIDHI_OCR ? 'animate-pulse text-saffron' : ''} />
            <span className="text-[8px] md:text-[10px] font-bold uppercase tracking-tighter">{t('nav_scanner')}</span>
          </button>

          <button onClick={() => handleNavigate(Tab.DASHBOARD)} className={`flex flex-col items-center gap-0.5 w-full h-full justify-center transition-colors ${activeTab === Tab.DASHBOARD ? 'text-templeRed dark:text-saffron' : 'text-gray-400 dark:text-stone-500'}`}>
            <LayoutDashboard size={18} />
            <span className="text-[8px] md:text-[10px] font-bold uppercase tracking-tighter">{t('nav_progress')}</span>
          </button>
          
          {isAdmin && (
            <button onClick={() => handleNavigate(Tab.ADMIN)} className={`flex flex-col items-center gap-0.5 w-full h-full justify-center transition-colors ${activeTab === Tab.ADMIN ? 'text-templeRed dark:text-saffron' : 'text-gray-400 dark:text-stone-500'}`}>
              <Shield size={18} />
              <span className="text-[8px] md:text-[10px] font-bold uppercase tracking-tighter">{t('nav_admin')}</span>
            </button>
          )}
        </div>
      </nav>
    </div>
    </FavoritesProvider>
  );
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

export const App: React.FC = () => (
  <ErrorBoundary>
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <LanguageProvider>
      <MusicProvider>
        <OfflineBanner />
        <AppContent />
        <Toaster position="top-center" richColors closeButton />
      </MusicProvider>
    </LanguageProvider>
    </GoogleOAuthProvider>
  </ErrorBoundary>
);

const PenTool = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 19 7-7 3 3-7 7-3-3z"/><path d="m18 13-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/><path d="m2 2 5 5"/><path d="m11 8 2 2"/></svg>
);
